import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../features/auth/auth-context';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { routeTo, paths } from '../../../routes/paths';
import styles from './AdminPortal.module.css';

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

export function AdminMandateDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const mandate = useQuery({
    queryKey: ['admin', 'mandates', id],
    queryFn: () => mandatesApi.getAdmin(id),
  });

  const checkStatus = useMutation({
    mutationFn: () => mandatesApi.checkStatusAdmin(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'mandates', id] });
    },
  });

  if (mandate.isPending) {
    return <PageSpinner label="Loading mandate" />;
  }

  if (mandate.isError) {
    return <Alert tone="error">{toDisplayMessage(mandate.error)}</Alert>;
  }

  const data = mandate.data;
  const canCheckStatus = can(PERMISSIONS.MANDATES_CHECK_STATUS);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Mandate {data.id.slice(0, 8)}</h1>
        <StatusBadge status={data.internalStatus} />
      </header>

      <p>
        <Link to={paths.adminMandates}>Back to mandates</Link>
      </p>

      {checkStatus.isError && <Alert tone="error">{toDisplayMessage(checkStatus.error)}</Alert>}
      {checkStatus.isSuccess && <Alert tone="success">Status check completed.</Alert>}

      <section className={styles.card} aria-labelledby="admin-mandate-details-heading">
        <h2 className={styles.cardTitle} id="admin-mandate-details-heading">
          Details
        </h2>

        <dl className={styles.definitionList}>
          <dt>Customer</dt>
          <dd>
            <Link to={routeTo.adminCustomer(data.customerId)}>{data.customerId.slice(0, 8)}</Link>
          </dd>
          <dt>Contract</dt>
          <dd>
            <Link to={routeTo.adminApplication(data.subscriptionId)}>
              {data.contractReference ?? data.subscriptionId.slice(0, 8)}
            </Link>
          </dd>
          <dt>Provider</dt>
          <dd>{data.provider}</dd>
          <dt>Provider status</dt>
          <dd>{data.providerStatus ?? '—'}</dd>
          <dt>Bank account</dt>
          <dd>{data.bankAccountMasked ?? data.bankAccountId.slice(0, 8)}</dd>
          <dt>Mandate code</dt>
          <dd>{data.mandateCode ?? '—'}</dd>
          <dt>Direct debit ID</dt>
          <dd>{data.directDebitId ?? '—'}</dd>
          <dt>Activation amount</dt>
          <dd>{data.activationAmount === null ? '—' : formatNgn(data.activationAmount)}</dd>
          <dt>Activation bank</dt>
          <dd>{data.activationBankName ?? '—'}</dd>
          <dt>Activation account</dt>
          <dd>{data.activationRecipientAccountMasked ?? '—'}</dd>
          <dt>Created</dt>
          <dd>{formatTimestamp(data.createdAt)}</dd>
          <dt>Activated</dt>
          <dd>{formatTimestamp(data.activatedAt)}</dd>
          <dt>Last check</dt>
          <dd>{formatTimestamp(data.lastStatusCheckAt)}</dd>
          <dt>Next check</dt>
          <dd>{formatTimestamp(data.nextStatusCheckAt)}</dd>
          <dt>Check count</dt>
          <dd>{data.statusCheckCount}</dd>
          <dt>Failure</dt>
          <dd>{data.failureReason ?? '—'}</dd>
        </dl>

        {canCheckStatus && (
          <div className={styles.actions}>
            <Button disabled={checkStatus.isPending} onClick={() => checkStatus.mutate()}>
              Check status with provider
            </Button>
          </div>
        )}
      </section>

      <section className={styles.card} aria-labelledby="status-history-heading">
        <h2 className={styles.cardTitle} id="status-history-heading">
          Status history
        </h2>

        {data.statusHistory.length === 0 && (
          <p className={styles.empty}>
            No status history yet. Use the current mandate status above to decide whether a
            permitted status check is needed.
          </p>
        )}

        {data.statusHistory.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Status history">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">From</th>
                  <th scope="col">To</th>
                  <th scope="col">Source</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.statusHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatTimestamp(entry.createdAt)}</td>
                    <td>{entry.fromStatus ?? '—'}</td>
                    <td>
                      <StatusBadge status={entry.toStatus} />
                    </td>
                    <td>{entry.source.replace(/_/g, ' ')}</td>
                    <td>{entry.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data.cancellationRequests.length > 0 && (
        <section className={styles.card} aria-labelledby="cancellation-history-heading">
          <h2 className={styles.cardTitle} id="cancellation-history-heading">
            Cancellation requests
          </h2>

          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Cancellation requests">
              <thead>
                <tr>
                  <th scope="col">Requested</th>
                  <th scope="col">Status</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.cancellationRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <Link to={routeTo.adminMandateCancellation(request.id)}>
                        {formatTimestamp(request.requestedAt)}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge status={request.status} />
                    </td>
                    <td>{request.requestReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
